import React, { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles } from 'lucide-react';
import { normalizeCustomerPhone } from '@/lib/phone';
import { trackEvent } from '@/lib/analytics/trackEvent';
import {
  formatCheckoutLoyaltyPoints,
  formatCheckoutRewardAvailability,
  getCheckoutLoyaltySummary,
  shouldFetchCheckoutLoyalty,
} from '@/lib/loyalty/checkoutLoyalty';

/**
 * Resumen de puntos y recompensas en checkout (solo lectura; sin canje automático).
 * @param {{ businessId: string, phone: string, className?: string }} props
 */
export function CheckoutLoyaltySummary({ businessId, phone, className = '' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const trackedKeyRef = useRef('');

  useEffect(() => {
    const bid = String(businessId || '').trim();
    if (!bid || !shouldFetchCheckoutLoyalty(phone)) {
      setLoading(false);
      setError(false);
      setData(null);
      trackedKeyRef.current = '';
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const summary = await getCheckoutLoyaltySummary({ businessId: bid, phone });
          if (cancelled) return;
          setData(summary);
          setError(false);

          const trackKey = `${bid}:${normalizeCustomerPhone(phone)}`;
          if (trackedKeyRef.current !== trackKey && summary?.success) {
            trackedKeyRef.current = trackKey;
            const rewards = Array.isArray(summary.available_rewards) ? summary.available_rewards : [];
            const redeemable = rewards.filter((r) => r.can_redeem).length;
            try {
              trackEvent('checkout_loyalty_summary_viewed', {
                customer_found: Boolean(summary.customer_found),
                rewards_count: rewards.length,
                redeemable_count: redeemable,
              });
            } catch {
              /* noop */
            }
          }
        } catch {
          if (!cancelled) {
            setData(null);
            setError(true);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [businessId, phone]);

  if (!shouldFetchCheckoutLoyalty(phone)) {
    return null;
  }

  const rewards = Array.isArray(data?.available_rewards) ? data.available_rewards : [];

  return (
    <div
      className={`rounded-lg border border-dashed border-primary/25 bg-primary/5 p-4 space-y-3 ${className}`.trim()}
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary shrink-0" aria-hidden />
        <p className="text-sm font-semibold text-foreground">Puntos y recompensas</p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          Revisando puntos…
        </p>
      ) : error ? (
        <p className="text-sm text-muted-foreground">No pudimos cargar tus puntos ahora.</p>
      ) : data?.success ? (
        <>
          {data.customer_found ? (
            <p className="text-sm text-foreground">
              Tienes{' '}
              <span className="font-semibold tabular-nums">
                {formatCheckoutLoyaltyPoints(data.ledger_balance)} pts
              </span>{' '}
              disponibles.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Aún no tienes puntos registrados con este local.
            </p>
          )}

          {rewards.length > 0 ? (
            <ul className="space-y-2">
              {rewards.map((reward) => {
                const avail = formatCheckoutRewardAvailability(reward);
                return (
                  <li
                    key={reward.id}
                    className="rounded-md border bg-background/80 px-3 py-2 text-sm space-y-1"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium leading-snug">{reward.name}</p>
                        {reward.description ? (
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                            {reward.description}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-xs font-medium tabular-nums text-muted-foreground">
                          {formatCheckoutLoyaltyPoints(reward.points_cost)} pts
                        </span>
                        <Badge variant={avail.variant} className="text-[10px] font-normal">
                          {avail.label}
                        </Badge>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">Este local aún no publicó recompensas por puntos.</p>
          )}
        </>
      ) : null}

      <p className="text-[11px] text-muted-foreground leading-snug border-t border-border/50 pt-2">
        Los canjes se realizan con el local. Este checkout aún no descuenta puntos automáticamente.
      </p>
    </div>
  );
}
